import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, MenuItem, Select, FormControl,
  InputLabel, Chip, SelectChangeEvent, List, ListItem, ListItemText,
  ListItemSecondaryAction, Autocomplete, Checkbox, Snackbar, Alert,
} from '@mui/material';
import { Add, Edit, People, Delete, Upload } from '@mui/icons-material';
import api from '../../api/client';

interface Unit {
  id: number; callsign: string; unit_type: string; unit_type_id: number | null;
  unit_type_name: string; agency_id: number;
  agency_name: string; status_name: string; status_color: string; taip_id: string;
}
interface Agency { id: number; name: string; }
interface UnitTypeOption { id: number; code: string; name: string; is_active: boolean; }
interface PersonnelOption { id: number; dsn: string; first_name: string; last_name: string; agency_name: string; }
interface UnitPersonnelEntry { personnel_id: number; dsn: string; first_name: string; last_name: string; role: string; }

export default function UnitsPage() {
  const [units, setUnits] = useState<Unit[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Unit | null>(null);
  const [unitTypes, setUnitTypes] = useState<UnitTypeOption[]>([]);
  const [form, setForm] = useState({ callsign: '', unit_type: 'patrol', unit_type_id: null as number | null, agency_id: 0, taip_id: '' });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [snack, setSnack] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Personnel assignment state
  const [personnelOpen, setPersonnelOpen] = useState(false);
  const [personnelUnit, setPersonnelUnit] = useState<Unit | null>(null);
  const [unitPersonnel, setUnitPersonnel] = useState<UnitPersonnelEntry[]>([]);
  const [allPersonnel, setAllPersonnel] = useState<PersonnelOption[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<PersonnelOption | null>(null);

  const load = () => {
    api.get('/units').then((r) => { setUnits(r.data); setSelected(new Set()); });
    api.get('/agencies').then((r) => setAgencies(r.data));
  };
  useEffect(() => {
    load();
    api.get('/unit-types').then((r) => setUnitTypes(r.data));
  }, []);

  const handleOpen = (unit?: Unit) => {
    if (unit) {
      setEditing(unit);
      setForm({ callsign: unit.callsign, unit_type: unit.unit_type, unit_type_id: unit.unit_type_id, agency_id: unit.agency_id, taip_id: unit.taip_id || '' });
    } else {
      setEditing(null);
      setForm({ callsign: '', unit_type: 'patrol', unit_type_id: null, agency_id: agencies[0]?.id || 0, taip_id: '' });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.patch(`/units/${editing.id}`, form);
      } else {
        await api.post('/units', form);
      }
      setOpen(false);
      load();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to save unit';
      alert(msg);
    }
  };

  const handlePersonnelOpen = async (unit: Unit) => {
    setPersonnelUnit(unit);
    setSelectedPerson(null);
    const [persRes, allRes] = await Promise.all([
      api.get(`/units/${unit.id}/personnel`),
      api.get('/personnel'),
    ]);
    setUnitPersonnel(persRes.data);
    setAllPersonnel(allRes.data);
    setPersonnelOpen(true);
  };

  const handleAssignPerson = async () => {
    if (!personnelUnit || !selectedPerson) return;
    await api.post(`/units/${personnelUnit.id}/assign-personnel/${selectedPerson.id}`);
    setSelectedPerson(null);
    const r = await api.get(`/units/${personnelUnit.id}/personnel`);
    setUnitPersonnel(r.data);
  };

  const handleRemovePerson = async (personnelId: number) => {
    if (!personnelUnit) return;
    await api.delete(`/units/${personnelUnit.id}/remove-personnel/${personnelId}`);
    const r = await api.get(`/units/${personnelUnit.id}/personnel`);
    setUnitPersonnel(r.data);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/units/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSnack(`Imported: ${r.data.created} created, ${r.data.skipped} skipped`);
      load();
    } catch { setSnack('Import failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleBulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} unit(s)? Personnel assignments will also be removed.`)) return;
    await api.post('/units/bulk-delete', { ids: Array.from(selected) });
    setSnack(`Deleted ${selected.size} unit(s)`);
    load();
  };

  const toggleSelect = (id: number) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === units.length) setSelected(new Set());
    else setSelected(new Set(units.map((u) => u.id)));
  };

  const assignedIds = new Set(unitPersonnel.map((p) => p.personnel_id));
  const availablePersonnel = allPersonnel.filter((p) => !assignedIds.has(p.id));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Units</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selected.size > 0 && (
            <Button startIcon={<Delete />} color="error" variant="outlined" onClick={handleBulkDelete}>
              Delete ({selected.size})
            </Button>
          )}
          <input type="file" accept=".csv" ref={fileRef} hidden onChange={handleImport} />
          <Button startIcon={<Upload />} variant="outlined" onClick={() => fileRef.current?.click()}>Import CSV</Button>
          <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Unit</Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"><Checkbox checked={units.length > 0 && selected.size === units.length} indeterminate={selected.size > 0 && selected.size < units.length} onChange={toggleAll} /></TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Agency</TableCell>
              <TableCell>TAIP ID</TableCell>
              <TableCell>Personnel</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={80} />
            </TableRow>
          </TableHead>
          <TableBody>
            {units.map((u) => (
              <TableRow key={u.id} selected={selected.has(u.id)}>
                <TableCell padding="checkbox"><Checkbox checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} /></TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{u.callsign}</TableCell>
                <TableCell>{u.unit_type_name || u.unit_type}</TableCell>
                <TableCell>{u.agency_name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{u.taip_id || '—'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handlePersonnelOpen(u)} title="Manage Personnel">
                    <People fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell><Chip size="small" label={u.status_name || 'Unset'} sx={{ bgcolor: u.status_color, color: '#fff' }} /></TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(u)}><Edit fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create Unit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Unit' : 'New Unit'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Unit" margin="normal" value={form.callsign} onChange={(e) => setForm({ ...form, callsign: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={form.unit_type_id !== null ? String(form.unit_type_id) : ''}
              label="Type"
              onChange={(e: SelectChangeEvent) => {
                const id = parseInt(e.target.value);
                const ut = unitTypes.find((t) => t.id === id);
                setForm({ ...form, unit_type_id: id, unit_type: ut?.code || form.unit_type });
              }}
            >
              {unitTypes.filter((t) => t.is_active).map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Agency</InputLabel>
            <Select value={String(form.agency_id)} label="Agency" onChange={(e: SelectChangeEvent) => setForm({ ...form, agency_id: Number(e.target.value) })}>
              {agencies.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="TAIP / Device ID (GPS Tracking)" margin="normal" value={form.taip_id} onChange={(e) => setForm({ ...form, taip_id: e.target.value })} placeholder="Unique device identifier for GPS tracking" helperText="This ID links the unit to GPS data from Traccar Client or a hardware AVL device" />
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 700, display: 'block', mb: 1, fontSize: 13 }}>📱 Traccar Client App (Recommended — iPhone & Android)</Typography>
            <Typography variant="body2" sx={{ fontSize: 12, mb: 0.5 }}>
              1. Install <strong>Traccar Client</strong> from the App Store or Google Play (free)<br />
              2. Open the app and configure:<br />
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, pl: 2, mb: 0.5 }}>
              • Device identifier: <strong>{form.taip_id || '(set TAIP ID above)'}</strong><br />
              • Server URL: <strong>{window.location.origin}/api/gps</strong><br />
              • Frequency: <strong>30 seconds</strong> (recommended)<br />
              • Location accuracy: <strong>High</strong>
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12, mb: 1.5 }}>
              3. Tap <strong>Start</strong> — GPS will feed to the CAD map in real-time
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5, fontSize: 12 }}>🔌 Hardware AVL Device (TAIP Protocol)</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              Address: <strong>{window.location.hostname}</strong><br />
              Port: <strong>4305</strong> (TCP)<br />
              Protocol: <strong>TAIP</strong> (RPV sentence with ;ID= trailer)<br />
              Device ID: <strong>{form.taip_id || '(set TAIP ID above)'}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Configure AVL hardware to send TAIP RPV sentences to this address/port. Set the device ID to match the TAIP ID above.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Personnel Assignment Dialog */}
      <Dialog open={personnelOpen} onClose={() => setPersonnelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Personnel — {personnelUnit?.callsign}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={availablePersonnel}
              value={selectedPerson}
              onChange={(_, v) => setSelectedPerson(v)}
              getOptionLabel={(o) => `${o.dsn} — ${o.last_name}, ${o.first_name}`}
              renderInput={(params) => <TextField {...params} label="Add Personnel" size="small" />}
            />
            <Button variant="contained" size="small" disabled={!selectedPerson} onClick={handleAssignPerson}>
              Assign
            </Button>
          </Box>
          {unitPersonnel.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No personnel assigned</Typography>
          ) : (
            <List dense>
              {unitPersonnel.map((p) => (
                <ListItem key={p.personnel_id}>
                  <ListItemText
                    primary={`${p.dsn} — ${p.last_name}, ${p.first_name}`}
                    secondary={p.role}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => handleRemovePerson(p.personnel_id)} sx={{ color: '#f44336' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPersonnelOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
